import { parseLawyerApprovalAction } from '@common/enums';
import type { ApprovalResponse } from '@common/types';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/system';
import { isAxiosError } from 'axios';
import { useLayoutEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import LexoLogo from '@/assets/lexo_logo.svg';
import ConfirmationPageWrapper from '@/components/ConfirmationPageWrapper';
import { getLawyerApprovalStatus } from '@/services/EmailApprovalService';
import LawyerResponseContent from './LawyerResponseContent';

const commonStyles = {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
};

export default function LawyerApprovalPage() {
    const theme = useTheme();
    const { instanceId, prospectId } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [approvalStatusData, setApprovalStatusData] = useState<ApprovalResponse>({
        Message: '',
        Status: '',
    });
    const [searchParams] = useSearchParams();
    const { neutral, primary } = theme.palette;
    const action = searchParams.get('Action');

    useLayoutEffect(() => {
        if (action !== 'Declined') {
            const handleLawyerApprovalStatus = async () => {
                if (!instanceId || !prospectId) return;
                setIsLoading(true);
                try {
                    const approvalResp = await getLawyerApprovalStatus(
                        instanceId,
                        prospectId,
                        parseLawyerApprovalAction(action),
                        null,
                    );
                    setApprovalStatusData(approvalResp.data);
                    setIsLoading(false);
                } catch (error: unknown) {
                    console.error(error);
                    setIsLoading(false);
                    if (isAxiosError(error)) {
                        setApprovalStatusData({
                            Status: error.response?.data?.Status || 'Failed',
                            Message: error.response?.data?.Message,
                        });
                    }
                }
            };
            handleLawyerApprovalStatus();
        } else {
            // set the status to success so at least the ClientResponseContent component can render the correct state
            setApprovalStatusData({
                Status: 'Success',
                Message: '',
            });
        }
    }, [instanceId, prospectId, action]);

    return (
        <ConfirmationPageWrapper>
            <Box
                sx={{
                    ...commonStyles,
                    maxWidth: '1216px',
                    width: '95%',
                    gap: '16px',
                    margin: '32px 0px',
                }}
            >
                <Box
                    sx={{
                        ...commonStyles,
                        gap: '16px',
                        margin: '0px',
                    }}
                >
                    <Box
                        sx={{
                            ...commonStyles,
                            display: 'flex',
                            flexDirection: 'row',
                            backgroundColor: neutral.white,
                            borderRadius: '8px',
                            padding: '32px',
                            gap: '32px',
                            alignItems: 'flex-start',
                            borderTop: `16px solid ${primary.P_450}`,
                        }}
                    >
                        {isLoading ? (
                            <Box>
                                <Skeleton variant="rounded" width={200} height={200} />
                            </Box>
                        ) : (
                            <Box>
                                <img
                                    alt="Lexo logo"
                                    style={{
                                        height: '200px',
                                        width: '200px',
                                        borderRadius: '4px',
                                    }}
                                    src={LexoLogo}
                                />
                            </Box>
                        )}
                        {isLoading ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}
                            >
                                <Skeleton variant="text" sx={{ fontSize: '24px', width: '50%' }} />
                                <Skeleton variant="text" sx={{ fontSize: '14px', width: '40%' }} />
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%', height: '100%' }}>
                                <LawyerResponseContent
                                    emailResponseAction={action || ''}
                                    status={approvalStatusData.Status || 'Failed'}
                                    failedMessage={approvalStatusData.Message}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </ConfirmationPageWrapper>
    );
}
